import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { RetryOnError, verifyToken } from "../utils/VerifyToken";
import { checkForAuthorization } from "utils/index";

export const CreateAnalysisBatch = () => {
    return useMutation(
        (formData) => {
            return axios({
                method: 'post',
                url: `${process.env.REACT_APP_BASE_URL}/pitch-deck-import/analysis/batch`,
                data: formData,
                headers: {
                    'Authorization': `Bearer ${checkForAuthorization()}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
        }
    );
};

export const FetchBatchStatus = (batch_id, options = {}) => {
    return useQuery(
        ['analysis-batch-status', batch_id],
        () => {
            return axios({
                method: 'get',
                url: `${process.env.REACT_APP_BASE_URL}/pitch-deck-import/batch/${batch_id}`,
                headers: {
                    'Authorization': `Bearer ${checkForAuthorization()}`,
                },
            });
        },
        {
            select: (data) => data?.data?.data,
            enabled: !!batch_id,
            refetchInterval: options.refetchInterval || false,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};

export const FetchAnalysisResults = (batch_id, params = {}) => {
    return useQuery(
        ['analysis-results', batch_id, params.page, params.search, params.status_filter],
        () => {
            const queryParams = new URLSearchParams();
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.search) queryParams.append('search', params.search);
            if (params.status_filter) queryParams.append('status_filter', params.status_filter);

            return axios({
                method: 'get',
                url: `${process.env.REACT_APP_BASE_URL}/pitch-deck-import/analysis/batches/${batch_id}/results?${queryParams.toString()}`,
                headers: {
                    'Authorization': `Bearer ${checkForAuthorization()}`,
                },
            });
        },
        {
            select: (data) => data?.data?.data,
            enabled: !!batch_id,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};

export const FetchAnalysisReports = (user_profile_id) => {
    return useQuery(
        ['analysis-reports', user_profile_id],
        () => {
            return axios({
                method: 'get',
                url: `${process.env.REACT_APP_BASE_URL}/pitch-deck-import/analysis/reports?user_profile_id=${user_profile_id}`,
                headers: {
                    'Authorization': `Bearer ${checkForAuthorization()}`,
                },
            });
        },
        {
            select: (data) => data?.data?.data,
            enabled: !!user_profile_id,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};

export const FetchAllAnalysisResults = (user_profile_id, params = {}) => {
    return useQuery(
        ['all-analysis-results', user_profile_id, params.page, params.search, params.status_filter, params.batch_id],
        () => {
            const queryParams = new URLSearchParams();
            queryParams.append('user_profile_id', user_profile_id);
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.search) queryParams.append('search', params.search);
            if (params.status_filter) queryParams.append('status_filter', params.status_filter);
            if (params.batch_id) queryParams.append('batch_id', params.batch_id);

            return axios({
                method: 'get',
                url: `${process.env.REACT_APP_BASE_URL}/pitch-deck-import/analysis/all-results?${queryParams.toString()}`,
                headers: {
                    'Authorization': `Bearer ${checkForAuthorization()}`,
                },
            });
        },
        {
            select: (data) => data?.data?.data,
            enabled: !!user_profile_id,
            refetchOnWindowFocus: false,
            refetchInterval: params.isProcessing ? 5000 : false,
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};

export const FetchAnalysisDetail = (file_id) => {
    return useQuery(
        ['analysis-detail', file_id],
        () => {
            return axios({
                method: 'get',
                url: `${process.env.REACT_APP_BASE_URL}/pitch-deck-import/analysis/files/${file_id}`,
                headers: {
                    'Authorization': `Bearer ${checkForAuthorization()}`,
                },
            });
        },
        {
            select: (data) => data?.data?.data,
            enabled: !!file_id,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};

export const InviteFromAnalysis = () => {
    return useMutation(
        ({ batch_id, ...payload }) => {
            return axios({
                method: 'post',
                url: `${process.env.REACT_APP_BASE_URL}/pitch-deck-import/analysis/batches/${batch_id}/invite`,
                data: payload,
                headers: {
                    'Authorization': `Bearer ${checkForAuthorization()}`,
                },
            });
        }
    );
};
